<template>
  <ContentTemplate title="Members" :container-size-small="false">
    <div class="row mb-5">
      <div class="col-3">
        <InputElement
            type="text"
            name="searchInput"
            :with-icon=true
            icon-type="fal"
            icon="Search"
            placeholder="Search"
            v-model="searchValue"
            @input="filterTable"
        />
      </div>
      <div class="col-3">
        <FilterDropdownElement
            title="Filter by roles"
            name="Roles"
            v-model="selectedRoles"
            :options="filterRoles"
            type="buttonselect"
            icon="Users"
            @change="filterTable"
        />
      </div>
      <div class="col-3">
        <FilterDropdownElement
            title="Filter by status"
            name="Status"
            v-model="selectedStatus"
            :options="filterStatus"
            type="buttonselect"
            icon="UserCheck"
            @change="filterTable"
        />
      </div>
      <div class="col-3 d-flex justify-content-end align-items-center">
        <ButtonElement class="btn--ms btn--accent"
                       @clickOnButton="goToInviteMemberPage()">
          <div>
            <LucideIcon icon="UserPlus" style="margin-right: 5px;"/>
            Add Member
          </div>
        </ButtonElement>
      </div>
    </div>

    <DataTable :value="wsMembers"
               :paginator="wsMembers.length > 10"
               :rows="10"
               paginator-template=" PageLinks  "
               :rows-per-page-options="[10,20,50]"
               current-page-report-template="Showing {first} to {last} of {totalRecords}"
               class="datatable-members"
               responsive-layout="scroll"
               :sort-field="'sortMembersByRole'"
    >
      <Column header="Members" class="datacol-1" style="overflow: initial">
        <template #body="slotProps">
          <div class="user" @click="goToDetailPage(slotProps.data)">
            <div class="user-avatar">

              <AvatarWidget
                  :image-key="slotProps.data.avatar"
                  :image-url="slotProps.data.avatar"
                  :social-avatar="slotProps.data.socialAvatar"
                  image-size="imageSM"
                  :full-name-avatar="slotProps.data.fullNameAvatar"
                  :alt="slotProps.data.fullNameAvatar"
                  :title="slotProps.data.fullNameAvatar">
              </AvatarWidget>

            </div>
            <div class="info">
              <span class="name">{{ slotProps.data.fullName }}</span>
              <span class="mail">{{ slotProps.data.userName }}</span>
            </div>
          </div>
        </template>
      </Column>
      <Column header="Roles" class="datacol-2">
        <template #body="slotProps">
          <TagElement :class="getRoleTagClass(slotProps.data.workspaceRole)">
            {{ formatRole(slotProps.data.workspaceRole) }}
          </TagElement>
        </template>
      </Column>
      <Column header="Invited" class="datacol-3">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.invitedDate) }}
        </template>
      </Column>
      <Column header="Last Activity" class="datacol-4">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.lastActivity) }}
        </template>
      </Column>
      <Column header="Status" class="datacol-5">
        <template #body="slotProps">
          {{ getReadableStatus(slotProps.data) }}
        </template>
      </Column>
      <Column header="Assigned" class="datacol-6">
        <template #body="slotProps">
          <div class="tag--default tag--neutral">{{ slotProps.data.assignedProject || 'Not assigned' }}</div>
        </template>
      </Column>
      <Column class="datacol-7">
        <template #body="slotProps">
          <ActionDropdownElement
              title="Actions"
              :options="filteredActions"
              type="button"
              icon="EllipsisVertical"
              class="actions-icon"
              @change="onActionSelected($event, slotProps.data)"
              @opened="beforeOpen(slotProps.data)"
          />
        </template>
      </Column>
    </DataTable>
  </ContentTemplate>
</template>
<script lang="ts">
import {defineComponent} from 'vue'
import {
  ActionDropdownElement,
  ButtonElement,
  ContentTemplate,
  FilterDropdownElement,
  InputElement,
  TagElement,
} from '@shared/components'
import {type DropdownNs, type PopupNs, SettingNs, WorkspaceNs} from '@shared/types'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(localizedFormat)
import {mapStores} from "pinia";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useWsMemberStore} from "@domains/settings/stores/workspace-member.store";
import {useGeneralStore} from "@shared/stores/general.store";
import AvatarWidget from "@shared/components/widgets/profile/avatar.widget.vue";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'MembersView',
  components: {
    AvatarWidget,
    ContentTemplate,
    InputElement,
    ButtonElement,
    FilterDropdownElement,
    ActionDropdownElement,
    DataTable,
    Column,
    TagElement

  },
  computed: {
    ...mapStores(useWorkspaceStore, useWsMemberStore, useGeneralStore),
  },
  data() {
    return {
      searchValue: '',
      selectedRoles: [] as DropdownNs.DropdownOptionsItem[],
      selectedStatus: [] as DropdownNs.DropdownOptionsItem[],
      wsMembers: [] as WorkspaceNs.Member[],
      filterRoles: [
        {
          name: 'Owner',
          value: 'OWNER'
        },
        {
          name: 'Admin',
          value: 'ADMIN'
        },
        {
          name: 'Member',
          value: 'MEMBER'
        }
      ] as DropdownNs.DropdownOptionsItem[],

      filterStatus: [
        {
          name: 'Active',
          value: 'ACTIVE'
        },
        {
          name: 'Inactive',
          value: 'INACTIVE'
        },
        {
          name: 'Pending',
          value: 'PENDING'
        }
      ] as DropdownNs.DropdownOptionsItem[],
      filteredActions: [] as DropdownNs.DropdownOptionsItem[]
    }
  },
  beforeMount() {
    const members = this.wsMemberStore.getWorkspaceMembers
    this.wsMembers = this.sortMembersByRole(members)
  },
  methods: {
    sortMembersByRole(members: WorkspaceNs.Member[]): WorkspaceNs.Member[] {
      const rolePriority = (role: string): number => {
        if (role === 'OWNER') return 1
        if (role === 'ADMIN') return 2
        if (role === 'MEMBER') return 3
        return 4
      }

      return members.slice().sort((a, b) => {
        const priorityDiff = rolePriority(a.workspaceRole) - rolePriority(b.workspaceRole)
        if (priorityDiff !== 0) return priorityDiff

        // Innerhalb gleicher Rolle alphabetisch nach Name sortieren
        return a.fullName.localeCompare(b.fullName)
      })
    },
    formatDate(value: string): string {
      if (value) {
        return dayjs(value).format('LL')
      }
      return ''
    },
    formatRole(role: string): string {
      if (!role) return '';
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    },
    getRoleTagClass(role: string): string {
      const roleFormatted = role.toUpperCase();
      if (roleFormatted === 'OWNER') return 'tag--orange';
      if (roleFormatted === 'ADMIN') return 'tag--blue';
      if (roleFormatted === 'MEMBER') return 'tag--neutral';
      return 'tag--neutral'; // fallback
    },
    reloadList() {
      const workspaceId = this.workspaceStore.getSelectedWorkspaceId
      this.wsMemberStore.loadWorkspaceMembers(workspaceId).then(() => {
        const members = this.wsMemberStore.getWorkspaceMembers
        this.wsMembers = this.sortMembersByRole(members)
      })
    },
    filterTable() {
      let filteredList = this.wsMemberStore.getWorkspaceMembers
      if (this.searchValue) {
        filteredList = filteredList.filter((item: WorkspaceNs.Member) => (item.fullName.toLowerCase().includes(this.searchValue.toLowerCase()) || item.userName.toLowerCase().includes(this.searchValue.toLowerCase())))
      }
      if (this.selectedRoles && this.selectedRoles.length > 0) {
        filteredList = filteredList.filter((item: WorkspaceNs.Member) => (this.selectedRoles.some((role) => (role.value === item.workspaceRole))))
      }
      if (this.selectedStatus && this.selectedStatus.length > 0) {
        filteredList = filteredList.filter((item: WorkspaceNs.Member) => (this.selectedStatus.some((status) => (status.value === item.memberStatus))))
      }
      this.wsMembers = filteredList
    },
    goToDetailPage(selectedMember: WorkspaceNs.Member) {
      this.$router.push({
        name: SettingNs.SettingRouteEnums.MEMBERDETAILS,
        params: {memberId: selectedMember.id}
      })
    },
    onActionSelected(action: DropdownNs.DropdownOptionsItem, selectedMember: WorkspaceNs.Member) {
      if (action.value === 'DETAILS') {
        this.goToDetailPage(selectedMember)
      } else if (action.value === 'DISABLED') {
        this.changeMemberStatus(selectedMember.id, 'INACTIVE')
      } else if (action.value === 'ENABLED') {
        this.changeMemberStatus(selectedMember.id, 'ACTIVE')
      } else if (action.value === 'RESEND_INVITATION') {
        this.resendInvitationMail(selectedMember)
      } else if (action.value === 'REMOVE') {
        this.removeWorkspaceMember(selectedMember)
      }
    },
    beforeOpen(selectedMember: WorkspaceNs.Member) {
      this.filteredActions = []
      if (selectedMember.memberStatus === 'ACTIVE') {
        this.filteredActions = [{
          name: 'Details',
          value: 'DETAILS',
          iconType: 'fal',
          icon: 'UserCog',
          closeOnSelect: true
        }]
        if (selectedMember.workspaceRole !== 'OWNER') {
          this.filteredActions.push({
            name: 'Disabled',
            value: 'DISABLED',
            iconType: 'fal',
            icon: 'UserX',
            closeOnSelect: true
          })
        }
      }
      if (selectedMember.memberStatus === 'INACTIVE') {
        this.filteredActions = [{
          name: 'Details',
          value: 'DETAILS',
          iconType: 'fal',
          icon: 'UserCog',
          closeOnSelect: true
        }]
        this.filteredActions.push({
          name: 'Enabled',
          value: 'ENABLED',
          iconType: 'fal',
          icon: 'UserCheck',
          closeOnSelect: true
        })
      }

      if (selectedMember.memberStatus === 'PENDING') {
        this.filteredActions.push({
          name: 'Resend Invitation',
          value: 'RESEND_INVITATION',
          iconType: 'fal',
          icon: 'Send',
          closeOnSelect: true
        })
        this.filteredActions.push({
          name: 'Remove',
          value: 'REMOVE',
          iconType: 'fal',
          icon: 'Trash2',
          closeOnSelect: true
        })
      }
    },
    getReadableStatus(selectedMember: WorkspaceNs.Member) {
      if (selectedMember.memberStatus === 'ACTIVE') return 'Active'
      else if (selectedMember.memberStatus === 'INACTIVE') return 'Disabled'
      else if (selectedMember.memberStatus === 'PENDING') return 'Invitation Pending'
    },
    changeMemberStatus(selectedMemberId: number, newStatus: string) {
      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        memberId: selectedMemberId,
        newStatus
      } as WorkspaceNs.ChangeWorkspaceMemberStatusRequest

      this.wsMemberStore.changeWorkspaceMemberStatus(payload).then(() => {
        toast.success('Member status has been updated')
        this.reloadList()
      })
    },
    resendInvitationMail(selectedMember: WorkspaceNs.Member) {
      const payload = {
        emailAddresses: [{
          email: selectedMember.userName,
          workspaceRole: selectedMember.workspaceRole
        }],
        workspaceId: this.workspaceStore.getSelectedWorkspaceId
      } as WorkspaceNs.InviteMembersRequest

      this.wsMemberStore.inviteMemberToWorkspace(payload).then(() => {
        toast.success('Invitation successfully sent')
      })
    },
    removeWorkspaceMember(selectedMember: WorkspaceNs.Member) {
      let popupMessage = ''
      let removeInvitation = false
      if (selectedMember.memberStatus === 'PENDING') {
        popupMessage = 'The workspace invitation will be removed.'
        removeInvitation = true
      } else {
        popupMessage = 'Removing this member from the workspace will also remove all associated project access.'
      }

      const messagePayload = {
        content: {
          title: 'Delete',
          message: popupMessage
        } as PopupNs.PopupContent,
        callback: {
          content: 'Remove',
          function: () => {
            const payload = {
              workspaceId: this.workspaceStore.getSelectedWorkspaceId,
              userName: selectedMember.userName,
              removeInvitation
            } as WorkspaceNs.RemoveWorkspaceMemberRequest

            this.wsMemberStore.deleteMemberFromWorkspace(payload).then(() => {
              const workspaceId = this.workspaceStore.getSelectedWorkspaceId

              this.wsMemberStore.loadWorkspaceMembers(workspaceId).then(() => {
                this.wsMembers = this.wsMemberStore.getWorkspaceMembers
              })
            })
          }
        } as PopupNs.PopupCallback
      } as PopupNs.Popup

      this.generalStore.showPopup(messagePayload)
    },
    goToInviteMemberPage() {
      this.$router.push({name: SettingNs.SettingRouteEnums.INVITETEAMMEMBERS})
    }
  }
})
</script>

<style lang="scss">

.datatable-members {
  td, th {
    padding: var(--space-8) !important;
  }

  th {
    font-size: var(--fs-12) !important;;
    font-weight: 500 !important;;
    color: var(--text-neutral) !important;;
  }

  .datacol-1 {
    width: 250px !important;;
  }

  .user {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .info {
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      flex-direction: column;
    }

    .name {
      width: 180px;
      color: var(--text-dark);
      margin-bottom: var(--space-negative-2);
      font-size: var(--fs-14);
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:hover {
        cursor: pointer;
      }
    }

    .mail {
      color: var(--text-neutral);
      font-size: var(--fs-12);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 180px;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    margin-right: var(--space-8);
    mask-image: url("data:image/svg+xml,%3Csvg clip-rule='evenodd' fill-rule='evenodd' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m18.947 18.947c-13.32 13.32-17.947 42.57-17.947 109.053s4.626 95.732 17.947 109.053c13.32 13.32 42.57 17.947 109.053 17.947s95.732-4.626 109.053-17.947c13.32-13.32 17.947-42.57 17.947-109.053s-4.626-95.732-17.947-109.053c-13.32-13.32-42.57-17.947-109.053-17.947s-95.732 4.626-109.053 17.947z' fill-rule='nonzero' stroke='%23000' transform='matrix(.992126 0 0 .992126 1.00787 1.00787)'/%3E%3C/svg%3E");
    mask-position: center center;
    mask-repeat: no-repeat;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    & > img {
      background-color: rgb(244, 244, 245);
      border-radius: $br-xs;
      display: block;
      height: 100%;
      margin: var(--space-0);
      padding: var(--space-0);
      position: relative;
      width: 100%;
      z-index: 0;
    }

    & > .text-avatar {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 40px;
      height: 40px;
      color: var(--light-100);
      background: var(--cyan-200);
      font-size: var(--fs-24);
      font-weight: 700;
      mask-image: url("data:image/svg+xml,%3Csvg clip-rule='evenodd' fill-rule='evenodd' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m18.947 18.947c-13.32 13.32-17.947 42.57-17.947 109.053s4.626 95.732 17.947 109.053c13.32 13.32 42.57 17.947 109.053 17.947s95.732-4.626 109.053-17.947c13.32-13.32 17.947-42.57 17.947-109.053s-4.626-95.732-17.947-109.053c-13.32-13.32-42.57-17.947-109.053-17.947s-95.732 4.626-109.053 17.947z' fill-rule='nonzero' stroke='%23000' transform='matrix(.992126 0 0 .992126 1.00787 1.00787)'/%3E%3C/svg%3E");
      mask-position: center center;
      mask-repeat: no-repeat;

      &:hover{
        background: var(--cyan-200);
      }

      h1 {
        color: var(--light-100);
        margin: var(--space-0);
        font-size: var(--fs-16);
        line-height: normal;
      }
    }
  }

  .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .assigned {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .p-datatable-wrapper {

    .p-datatable-tbody {
      tr {
        font-size: var(--fs-12);
        color: var(--text-neutral);
        border-radius: $br-sm;

        &:hover {
          border-radius: $br-sm;
          box-shadow: var(--shadow-lg);
          z-index: 1;
          position: relative;
        }

        td:first-child {
          border-top-left-radius: $br-sm;
          border-bottom-left-radius: $br-sm;
        }

        td:last-child {
          border-top-right-radius: $br-sm;
          border-bottom-right-radius: $br-sm;
        }
      }
    }
  }
}

.p-paginator {
  .p-paginator-pages {
    .p-paginator-page {
      background-color: transparent;
      border: 0 none;
      color: var(--neutral-200);
      min-width: 32px;
      height: 32px;
      margin: 0px;
      transition: box-shadow 0.2s;
      border-radius: $br-sm;

      &:not(.p-highlight):hover {
        background: var(--neutral-100);
        border-color: var(--neutral-100);
        color: var(--text-dark);
      }

      &.p-highlight {
        background: var(--accent-100);
        border-color: var(--accent-100);
        color: var(--light-100);
      }
    }
  }
}

.p-datatable-responsive-scroll > .p-datatable-wrapper {
  overflow-x: initial;
}

</style>
